import { FC, useEffect, useState } from 'react';
import styles from './CreateUserModal.module.css';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Modal from '../common/Modal/Modal';
import TextField from '../common/Form/FormFields/TextField/TextField';
import LoadingButton from '../common/LoadingButton/LoadingButton';
import Button from '../common/Button';
import Shape from '../../@types/Shape';
import axios from '../../api/axios';
import { RoleType, UserAttributes, RoleTypeListAdmin, RoleTypeList, RoleTypeLabel, User } from '../../@types/Auth';
import SelectOptions from '../common/Form/FormFields/Select/Select';
import useAuth from '../../hooks/useAuth';
import { Stack, Typography } from '@mui/material';
import CheckBox from '../common/Form/FormFields/CheckBox/CheckBox';
import { flatten } from 'lodash';
import { isMadJaxAdmin } from '../../utils/isMadJaxAdmin';
import { isDealerAdmin } from '../../utils/isDealerAdmin';


interface CreateUserModalProps {
  closeModal: () => void;
  isVisible: boolean;
  dealerId?: string
}

const CreateUserModal: FC<CreateUserModalProps> = ({ dealerId, closeModal, isVisible }) => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit = async (data: Partial<User>) => {
    try {
      setLoading(true);
      await axios.post(`/user`, data);
      toast.success(`Account Created`);
      closeModal();
    } catch (error: any) {
      console.log(error);
      toast.error(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  const onError = (errors: any) => {
    console.log(errors);
    const errorsFlattened: any = flatten(errors);
    const requiredErrors = Object.fromEntries(Object.entries(errorsFlattened).filter(([key, value]) => key.includes('.type')));
    if (Object.entries(requiredErrors).length <= 2) {
      for (const key in requiredErrors) {
        let keySplit = key.split('.');
        keySplit.pop();
        let formField = '';
        for (const fieldPart of keySplit) {
          formField += fieldPart.split(/([A-Z][a-z]+)/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
        }
        toast.error(`Please check [${formField.trim()}] field for errors`);
      }
    } else {
      toast.error('Please check form for errors');
    }
  };

  const createUserSchema = yup.object<Shape<Partial<User>>>({
    pk: yup.string().required(),
    email: yup.string()
      .strict()
      .trim('email address cannot include leading or trailing spaces')
      .email().email('Please enter a valid email address')
      .required(`Please enter an email address`),
    attributes: yup.object<Shape<UserAttributes>>({
      firstName: yup.string().required(`Please enter the user's first name`),
      lastName: yup.string().required(`Please enter the user's last name`),
      phone: yup.string().optional(),
      accountStatus: yup.string().required().default('unregistered'),
      type: yup.string().required('Please select the permission level for the user'),
      leadsContact: yup.boolean().optional()
    }),
  });

  const defaultValues: Partial<User> = {
    pk: `DEALERID#${dealerId ?? currentUser?.companyAttributes?.dealerId}`
  }

  const userRoles: RoleTypeLabel[] = currentUser?.attributes?.type !== RoleType.Admin ? RoleTypeList : RoleTypeListAdmin;

  const methods = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(createUserSchema),
    resetOptions: {
      keepValues: true
    }
  });

  const { handleSubmit, watch } = methods;

  const [roleDescription, setRoleDescription] = useState<string>('');
  const selectedUserRole = watch('attributes.type');

  useEffect(() => {
    const role = userRoles.find((userRole) => userRole.value === selectedUserRole);
    setRoleDescription(role?.description ?? '');
  }, [selectedUserRole, setRoleDescription]);

  return (
    <>
      {isVisible &&
        <Modal title={`Create User`} closeModal={closeModal}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit, onError)}>
              <Stack direction="column" spacing={2}>
                <TextField name="email" label="Email" />
                <TextField name="attributes.firstName" label="First Name" />
                <TextField name="attributes.lastName" label="Last Name" />
                <TextField name="attributes.phone" label="Phone" />
                <SelectOptions name="attributes.type" disabled={!isMadJaxAdmin(currentUser) && !isDealerAdmin(currentUser)} label="Permission Level" options={userRoles} />
                <Typography sx={{ marginBottom: '36px' }}>{roleDescription}</Typography>
                <CheckBox
                  name="attributes.leadsContact"
                  label="Leads Contact"
                  isTrueFalse={true}
                  defaultValue={0} />
                <div className={styles.FormActions}>
                  <LoadingButton loading={loading} className={styles.SubmitButton} type="submit">Submit</LoadingButton>
                  <Button className={styles.CancelButton} onClick={closeModal} type="button">Cancel</Button>
                </div>
              </Stack>
            </form>
          </FormProvider>
        </Modal >
      }
    </>
  );
};

export default CreateUserModal;
